import { useMemo } from 'react';
import { MaybeNil } from '../interfaces';
import { IReactSelectOption } from '../types';

interface IProps<T> {
  options: T[];
  selectedItemKey: MaybeNil<string>;
  /**
   * properties[0] is the property name for value
   * properties[1] is the property name for label
   * For example: ['key', 'text']
   */
  properties: [keyof T, keyof T];
}

interface IResult<T> {
  formattedOptions: Array<T & IReactSelectOption>;
  formattedSelectedOption: (T & IReactSelectOption) | null;
}

/**
 * This hook will generate formatted options and selected item with the format required for react-select
 * it will return the option like {...option, label: string, name: string}
 * This hook works well in non-async react-select menu
 */
export default <T, K extends keyof T, O extends { [P in K]: string }>({
  options,
  selectedItemKey,
  properties,
}: IProps<O>): IResult<O> => {
  const formattedOptions = useMemo(() => {
    return options.map<O & IReactSelectOption>((option) => {
      return {
        ...option,
        value: option[properties[0]],
        label: option[properties[1]],
      };
    });
  }, [options, properties]);

  const formattedSelectedOption = useMemo(() => {
    // find the selected item in formattedOptions
    const selectedItem = formattedOptions.find((optionItem) => {
      return optionItem.value === selectedItemKey;
    });
    return selectedItem || null;
  }, [formattedOptions, selectedItemKey]);

  return { formattedOptions, formattedSelectedOption };
};
